import { usePrivatePolicySettingsStore } from "../usePrivatePolicySettingsStore/usePrivatePolicySettingsStore";
import { Box, Button, Divider, Modal, Stack, Switch, Tooltip, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { inverseNavyButton, navyButton } from "../../../../styles/mui-overrides";
import { useState } from "react";
import dayjs from "dayjs";
import DatePicker from "../../../../components/lib/SafeLeaseDatePicker";
import ConfirmDialog from "../../../../shared/ConfirmDialog";
import { PrivatePolicyComplianceConfig } from "../../../../utilities/generated/gql-types";

type PrivatePolicyConfigurationModalProps = {
  handleApply: () => void;
  handleClose: () => void;
  privatePolicyConfigs: PrivatePolicyComplianceConfig[]; // the list of private policy configs for the selected relationship.
};

function PrivatePolicyComplianceModalMenu({ handleApply, handleClose, privatePolicyConfigs }: PrivatePolicyConfigurationModalProps) {
  const [fmsEnforcementDateError, setFmsEnforcementDateError] = useState(false);
  const [intEnforcementDateError, setIntEnforcementDateError] = useState(false);
  const [ppeNewDateError, setPpeNewDateError] = useState(false);

  const {
    configMenuOpen,
    internalEnforcementEnabled,
    setInternalEnforcementEnabled,
    fmsEnforcementEnabled,
    setFmsEnforcementEnabled,
    selectedFmsEnforcementDate,
    setSelectedFmsEnforcementDate,
    selectedIntEnforcementDate,
    setSelectedIntEnforcementDate,
    firstChargeNextBillingCycle,
    setFirstChargeNextBillingCycle,
    tenantPolicyExpirationRemindersEnabled,
    setTenantPolicyExpirationRemindersEnabled,
    tenantPolicyExpirationRemindersEnabledDefault,
    setTenantPolicyExpirationRemindersEnabledDefault,
    selectedPpeNewDate,
    setSelectedPpeNewDate,
    confirmationDialogOpen,
    setConfirmationDialogOpen,
    ppeNewEnabled,
    setPpeNewEnabled,
  } = usePrivatePolicySettingsStore((state) => ({
    configMenuOpen: state.configMenuOpen,
    internalEnforcementEnabled: state.internalEnforcementEnabled,
    setInternalEnforcementEnabled: state.setInternalEnforcementEnabled,
    fmsEnforcementEnabled: state.fmsEnforcementEnabled,
    setFmsEnforcementEnabled: state.setFmsEnforcementEnabled,
    selectedFmsEnforcementDate: state.selectedFmsEnforcementDate,
    setSelectedFmsEnforcementDate: state.setSelectedFmsEnforcementDate,
    selectedIntEnforcementDate: state.selectedIntEnforcementDate,
    setSelectedIntEnforcementDate: state.setSelectedIntEnforcementDate,
    firstChargeNextBillingCycle: state.firstChargeNextBillingCycle,
    setFirstChargeNextBillingCycle: state.setFirstChargeNextBillingCycle,
    tenantPolicyExpirationRemindersEnabled: state.tenantPolicyExpirationRemindersEnabled,
    setTenantPolicyExpirationRemindersEnabled: state.setTenantPolicyExpirationRemindersEnabled,
    tenantPolicyExpirationRemindersEnabledDefault: state.tenantPolicyExpirationRemindersEnabledDefault,
    setTenantPolicyExpirationRemindersEnabledDefault: state.setTenantPolicyExpirationRemindersEnabledDefault,
    selectedPpeNewDate: state.selectedPpeNewDate,
    setSelectedPpeNewDate: state.setSelectedPpeNewDate,
    confirmationDialogOpen: state.confirmationDialogOpen,
    setConfirmationDialogOpen: state.setConfirmationDialogOpen,
    ppeNewEnabled: state.ppeNewEnabled,
    setPpeNewEnabled: state.setPpeNewEnabled,
  }));

  const handleDateChange = (
    date: Date,
    setError: React.Dispatch<React.SetStateAction<boolean>>,
    setDate: (date: dayjs.Dayjs | null) => void,
    dateKeyToCompareAgainstExistingConfigs: string,
  ) => {
    const dayJsDate = dayjs(date);
    if (dayJsDate.isValid()) {
      if (dayJsDate.isBefore(dayjs(), "day")) {
        if (privatePolicyConfigs.some((config) => dayjs(config[dateKeyToCompareAgainstExistingConfigs]).isSame(dayJsDate, "day"))) {
          setError(false);
          setDate(dayJsDate);
        } else {
          // Date is in the past and no existing configs have used this date
          setError(true);
        }
      } else {
        setError(false);
        setDate(dayJsDate);
      }
    } else {
      // Parsing error or invalid date
      setError(true);
    }
  };

  const handlePpeNewToggle = () => {
    if (ppeNewEnabled && selectedPpeNewDate && selectedPpeNewDate.startOf("day").isBefore(dayjs().startOf("day"))) {
      setConfirmationDialogOpen(true);
    } else {
      setPpeNewEnabled(!ppeNewEnabled);
    }
  };

  const handlePpeNewConfirm = () => {
    setPpeNewEnabled(false);
    setConfirmationDialogOpen(false);
  };

  const handlePpeNewCancel = () => {
    setConfirmationDialogOpen(false);
  };

  return (
    <Modal open={configMenuOpen} onClose={handleClose} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
      <>
        <Stack
          spacing={3}
          sx={{
            backgroundColor: "rgba(255, 255, 255, 1)",
            borderRadius: "5px",
            padding: "32px",
          }}
        >
          <Stack spacing={3}>
            {/* FMS Enforcement Toggle */}
            <Stack spacing={1}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Stack alignItems="center" spacing={1} direction="row">
                  <Typography variant="h6">FMS Enforcement</Typography>
                  <Tooltip
                    placement="right"
                    title="Enable/disable private policy compliance for private policies that were submitted on the FMS directly instead of going through SafeLease."
                  >
                    <InfoOutlinedIcon color="disabled" />
                  </Tooltip>
                </Stack>
                <Switch checked={fmsEnforcementEnabled} onChange={() => setFmsEnforcementEnabled(!fmsEnforcementEnabled)} />
              </Box>

              {/* FMS Enforcement Date Picker */}
              {fmsEnforcementEnabled && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <DatePicker
                    label="Date"
                    disabled={!fmsEnforcementEnabled}
                    value={selectedFmsEnforcementDate}
                    onChange={(newDate: Date) =>
                      handleDateChange(newDate, setFmsEnforcementDateError, setSelectedFmsEnforcementDate, "fmsEnforcementDate")
                    }
                    slotProps={{
                      textField: {
                        error: fmsEnforcementDateError,
                        helperText: fmsEnforcementDateError ? "Date must be valid and in the future" : "",
                      },
                    }}
                    disablePast
                  />
                  <Tooltip
                    placement="right"
                    title="This is the date that SafeLease will begin enforcing private policy compliance that is set for the private policies that are found in the FMS."
                  >
                    <InfoOutlinedIcon color="disabled" />
                  </Tooltip>
                </Stack>
              )}
            </Stack>
            <Divider variant="middle" />

            {/* Internal Enforcement Toggle */}
            <Stack spacing={1}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Stack alignItems="center" spacing={1} direction="row">
                  <Typography variant="h6">Internal Enforcement</Typography>
                  <Tooltip
                    placement="right"
                    title="Enable/disable private policy compliance for private policies that were submitted through SafeLease. This will also require all private policies to be submitted directly through Safelease. Any private policies from the fms will be removed."
                  >
                    <InfoOutlinedIcon color="disabled" />
                  </Tooltip>
                </Stack>
                <Switch checked={internalEnforcementEnabled} onChange={() => setInternalEnforcementEnabled(!internalEnforcementEnabled)} />
              </Box>

              {/* Internal Enforcement Date Picker */}
              {internalEnforcementEnabled && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <DatePicker
                    label="Date"
                    disabled={!internalEnforcementEnabled}
                    value={selectedIntEnforcementDate}
                    onChange={(newDate: Date) =>
                      handleDateChange(newDate, setIntEnforcementDateError, setSelectedIntEnforcementDate, "intEnforcementDate")
                    }
                    slotProps={{
                      textField: {
                        error: intEnforcementDateError,
                        helperText: intEnforcementDateError ? "Date must be valid and in the future" : "",
                      },
                    }}
                    disablePast
                  />
                  <Tooltip
                    placement="right"
                    title="This is the date that SafeLease will begin enforcing private policy compliance that is set for the private policies that are submitted directly through SafeLease. This will also be the day that existing FMS private policies will be removed."
                  >
                    <InfoOutlinedIcon color="disabled" />
                  </Tooltip>
                </Stack>
              )}
            </Stack>
            <Divider variant="middle" />

            {/* PPE New Toggle */}
            <Stack spacing={1}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Stack alignItems="center" spacing={1} direction="row">
                  <Typography variant="h6">PPE New</Typography>
                  <Tooltip
                    placement="right"
                    title="Enable/Disable Private Policy Enforcement for new move-ins. This will require that all new move-ins have a private policy submitted through SafeLease."
                  >
                    <InfoOutlinedIcon color="disabled" />
                  </Tooltip>
                </Stack>
                <Switch checked={ppeNewEnabled} onChange={handlePpeNewToggle} />
              </Box>
            </Stack>

            {/* PPE New Date Picker */}
            {ppeNewEnabled && (
              <Stack direction="row" spacing={1} alignItems="center">
                <DatePicker
                  label="Date"
                  disabled={!ppeNewEnabled}
                  value={selectedPpeNewDate ? dayjs(selectedPpeNewDate) : null}
                  onChange={(newDate: Date) => handleDateChange(newDate, setPpeNewDateError, setSelectedPpeNewDate, "ppeNewDate")}
                  slotProps={{
                    textField: {
                      error: ppeNewDateError,
                      helperText: ppeNewDateError ? "Date must be valid and in the future" : "",
                    },
                  }}
                  disablePast
                />
                <Tooltip placement="right" title="All new move-ins after this date must have a private policy submitted through SafeLease.">
                  <InfoOutlinedIcon color="disabled" />
                </Tooltip>
              </Stack>
            )}
            <Divider variant="middle" />

            {/* Charge next billing cycle toggle */}
            <Stack spacing={1}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Stack alignItems="center" spacing={1} direction="row">
                  <Typography variant="h6">Charge next billing cycle</Typography>
                  <Tooltip
                    placement="right"
                    title="When FMS or Internal enforcement goes live, non-compliant private policies be removed immediately and units will be enrolled effective the first day of the next billing cycle."
                  >
                    <InfoOutlinedIcon color="disabled" />
                  </Tooltip>
                </Stack>
                <Switch
                  checked={firstChargeNextBillingCycle}
                  onChange={() => setFirstChargeNextBillingCycle(!firstChargeNextBillingCycle)}
                />
              </Box>
            </Stack>
            <Divider variant="middle" />

            {/* Enable expiring emails toggle */}
            <Stack spacing={1}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Stack alignItems="center" spacing={1} direction="row">
                  <Typography variant="h6">Enable Expiring Emails</Typography>
                  <Tooltip placement="right" title="Remind the tenant 30 days before their policy expires via email.">
                    <InfoOutlinedIcon color="disabled" />
                  </Tooltip>
                </Stack>
                <Switch
                  checked={tenantPolicyExpirationRemindersEnabled}
                  onChange={() => setTenantPolicyExpirationRemindersEnabled(!tenantPolicyExpirationRemindersEnabled)}
                />
              </Box>
            </Stack>

            {/* Expiring emails check by default toggle */}
            {tenantPolicyExpirationRemindersEnabled && (
              <Stack spacing={1}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Stack alignItems="center" spacing={1} direction="row">
                    <Typography variant="h6">Check by default</Typography>
                  </Stack>
                  <Switch
                    checked={tenantPolicyExpirationRemindersEnabledDefault}
                    onChange={() => setTenantPolicyExpirationRemindersEnabledDefault(!tenantPolicyExpirationRemindersEnabledDefault)}
                  />
                </Box>
              </Stack>
            )}
          </Stack>

          {/* Apply and cancel buttons */}
          <Stack direction="row" spacing={2}>
            <Button disableElevation sx={{ ...inverseNavyButton, width: "165px" }} variant="contained" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              disableElevation
              sx={{ ...navyButton, width: "165px" }}
              variant="contained"
              onClick={handleApply}
              disabled={fmsEnforcementDateError || intEnforcementDateError}
            >
              Apply
            </Button>
          </Stack>
        </Stack>

        <ConfirmDialog
          open={confirmationDialogOpen}
          title="Disable PPE New"
          description="Are you sure you want to disable PPE New? This will clear the PPE New date and cannot be easily re-enabled if the date is in the past."
          onClose={handlePpeNewCancel}
          onConfirm={handlePpeNewConfirm}
        />
      </>
    </Modal>
  );
}

export default PrivatePolicyComplianceModalMenu;
